/**
 * Client workspace · Team tab — Task 109.
 *
 * The "client team" is the set of active `client_assignments` rows for
 * the client — the agency employees assigned to it. Each member carries
 * their global agency role(s) and a server-derived `disciplines`
 * eligibility array (content / design / video) that production
 * auto-routing (task 110) will consume.
 *
 * Read: `list-client-team` (the roster) + `list-user-roles` (the pool of
 *       agency employees available to add).
 * Write: `assign-client-team-member`, `end-client-team-assignment`.
 *
 * Audience: leadership only (`owner` / `marketing_manager`). The tab is
 * hidden for everyone else in `ClientWorkspaceLayout`; this page also
 * self-guards so a direct URL hit renders `PermissionDeniedState`.
 * Rationale: the `client_assignments_select` RLS policy only returns the
 * full roster to leadership / account_manager — a non-leadership
 * employee would see a misleading one-row list. RLS + the edge
 * functions are the load-bearing security gates; this matrix is UX-only.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { isApiError, KmiApiError } from '../../api/client';
import {
  assignClientTeamMember,
  endClientTeamAssignment,
  grantClientTeamPermission,
  listClientTeam,
  listClientTeamPermissions,
  listPermissions,
  listUserRoles,
  revokeClientTeamPermission,
} from '../../api/endpoints';
import type {
  ClientTeamMember,
  ClientTeamPermissionEffect,
  ClientTeamPermissionOverride,
  ContentDiscipline,
  ListClientTeamData,
  PermissionCatalogRow,
  UserRoleRow,
} from '../../api/types';
import { useMeContext } from '../../context/MeContextProvider';
import { isLeadership } from '../../permissions';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PermissionDeniedState,
} from '../../components/states';

type LoadState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly team: ListClientTeamData;
      readonly pool: ReadonlyArray<AvailableEmployee>;
    }
  | { readonly status: 'error'; readonly error: unknown };

interface AvailableEmployee {
  readonly userId: string;
  readonly label: string;
  /**
   * Task 125 — agency role label(s) the employee holds, surfaced in the
   * dropdown option text so a leader picking "Ahmad" vs "Sarah" can
   * see "Ahmad — مصمم" vs "Sarah — كاتب محتوى, مدير حسابات" and pick
   * the right person. `listUserRoles` returns role metadata per row;
   * `buildAvailablePool` collects the unique localised labels per user
   * after deduping by `user_id`.
   */
  readonly roleLabels: ReadonlyArray<string>;
}

/**
 * Task 126 (gate) + Task 132 (un-gate) — the Task 122A permission-
 * overrides drawer was gated behind this flag until its backend
 * shipped. As of 2026-05-17 the migration
 * `20260525120000_client_scoped_permission_overrides.sql` is applied
 * to project ucztnssvfllqwxotcpfb, all 4 new 122A EFs
 * (`list-permissions`, `list-client-team-permissions`,
 * `grant-client-team-permission`, `revoke-client-team-permission`)
 * are deployed v1 ACTIVE with verify_jwt=false, and
 * `end-client-team-assignment` is redeployed at v3 carrying the
 * CSP-1.7 cascade-revoke. The flag is now `true` and the drawer is
 * live on production.
 *
 * The constant is intentionally retained as an emergency kill-switch.
 * If a production incident surfaces (e.g. a 122A EF starts
 * misbehaving), a hot-fix PR flipping the value back to `false`
 * re-gates the drawer in one line without needing to delete or
 * re-author any of the wired drawer / form / row components.
 * `ClientTeamPage.task126.test.ts` pins this flag to `true` so the
 * post-un-gate regression lock is automatic.
 */
const TEAM_PERMISSION_OVERRIDES_UI_ENABLED = true;

/**
 * Task 122A — page-level permissions-catalog cache. The override-grant
 * form (per-row drawer) needs `list-permissions` to populate its
 * picker. We load it ONCE per page open, lazily — only when the first
 * row drawer opens. Sharing the cache across rows keeps the FE
 * round-trip count at O(1) regardless of how many rows the leader
 * expands. While gated off (Task 126) the cache stays declared but
 * `ensureCatalog` is never invoked; lint stays clean because the cache
 * is still passed to the drawer component, which itself is never
 * mounted under the gate.
 */
interface PermissionsCatalogState {
  readonly status: 'idle' | 'loading' | 'ready' | 'error';
  readonly catalog: ReadonlyArray<PermissionCatalogRow> | null;
  readonly error: unknown;
}

export function ClientTeamPage() {
  const { clientId } = useParams();
  const { me } = useMeContext();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  // Task 122A — lazy catalog cache.
  const [catalog, setCatalog] = useState<PermissionsCatalogState>({
    status: 'idle',
    catalog: null,
    error: null,
  });
  const ensureCatalog = useCallback(() => {
    setCatalog((prev) => {
      if (prev.status === 'loading' || prev.status === 'ready') return prev;
      listPermissions()
        .then((data) => setCatalog({ status: 'ready', catalog: data.permissions, error: null }))
        .catch((err: unknown) => setCatalog({ status: 'error', catalog: null, error: err }));
      return { status: 'loading', catalog: null, error: null };
    });
  }, []);

  const isLeader = !!me && !me.system_admin && isLeadership(me);

  useEffect(() => {
    if (!clientId || !isLeader) return;
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    Promise.all([
      listClientTeam({ clientId, signal: ctrl.signal }),
      // The add-member pool is a soft dependency: if `list-user-roles`
      // fails (RLS, network), the roster still renders — the picker just
      // shows no candidates rather than collapsing the whole page.
      listUserRoles({ signal: ctrl.signal }).catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        if (isApiError(err) && (err.code === 'permission_denied' || err.code === 'not_found')) {
          return { user_roles: [] as ReadonlyArray<UserRoleRow> };
        }
        throw err;
      }),
    ])
      .then(([team, roles]) => {
        if (ctrl.signal.aborted) return;
        const teamUserIds = new Set(team.members.map((m) => m.user_id));
        setState({
          status: 'ready',
          team,
          pool: buildAvailablePool(roles.user_roles, teamUserIds),
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, isLeader, reloadKey]);

  if (!clientId || !me) return null;

  // Self-guard. The Team tab is hidden from non-leadership in the
  // workspace layout; this catches a direct URL navigation.
  if (!isLeader) {
    return <PermissionDeniedState />;
  }

  if (state.status === 'loading') return <LoadingState title="جاري تحميل فريق العميل…" />;
  if (state.status === 'error') {
    return (
      <ErrorState
        title="تعذر تحميل فريق العميل"
        error={state.error}
        onRetry={() => setReloadKey((n) => n + 1)}
      />
    );
  }

  const refresh = () => setReloadKey((n) => n + 1);

  return (
    <div className="stack-md">
      <section className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <div>
            <h2>فريق العميل</h2>
            <p className="muted" style={{ margin: '6px 0 0' }}>
              موظفو الوكالة المعيّنون لهذا العميل. الدور العام لكل موظف يحدّد التخصصات التي يمكن
              توجيه مهام الإنتاج إليه فيها.
            </p>
          </div>
          <span className="meta">{state.team.members.length}</span>
        </div>

        {state.team.members.length === 0 ? (
          <EmptyState
            title="لا يوجد أعضاء فريق لهذا العميل بعد"
            description="أضف موظفي الوكالة الذين سيعملون على هذا العميل من النموذج أدناه."
          />
        ) : (
          <TeamMemberList
            clientId={clientId}
            members={state.team.members}
            catalog={catalog}
            ensureCatalog={ensureCatalog}
            onChanged={refresh}
          />
        )}
      </section>

      <AddMemberForm clientId={clientId} pool={state.pool} onAdded={refresh} />
    </div>
  );
}

// ── Member list ──────────────────────────────────────────────────────

function TeamMemberList({
  clientId,
  members,
  catalog,
  ensureCatalog,
  onChanged,
}: {
  readonly clientId: string;
  readonly members: ReadonlyArray<ClientTeamMember>;
  readonly catalog: PermissionsCatalogState;
  readonly ensureCatalog: () => void;
  readonly onChanged: () => void;
}) {
  return (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {members.map((m) => (
        <TeamMemberRow
          key={m.assignment_id}
          clientId={clientId}
          member={m}
          catalog={catalog}
          ensureCatalog={ensureCatalog}
          onChanged={onChanged}
        />
      ))}
    </ul>
  );
}

function TeamMemberRow({
  clientId,
  member,
  catalog,
  ensureCatalog,
  onChanged,
}: {
  readonly clientId: string;
  readonly member: ClientTeamMember;
  readonly catalog: PermissionsCatalogState;
  readonly ensureCatalog: () => void;
  readonly onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const name = member.profile?.full_name?.trim() || member.profile?.email || member.user_id;
  const roleLabels = member.roles.map((r) => r.name_ar || r.name_en || r.key);

  async function remove() {
    setError(null);
    setRemoving(true);
    try {
      await endClientTeamAssignment({ input: { assignment_id: member.assignment_id } });
      onChanged();
    } catch (err) {
      setRemoving(false);
      setConfirming(false);
      setError(mutationErrorMessage(err));
    }
  }

  function toggleDrawer() {
    // Task 126 — gated. With the flag off the toggle button is not
    // rendered (see JSX below) so this handler is unreachable in
    // production. The guard here is belt-and-braces: if a future
    // refactor accidentally exposes the button via some other path,
    // the handler still no-ops instead of firing the missing-backend
    // catalog load.
    if (!TEAM_PERMISSION_OVERRIDES_UI_ENABLED) return;
    setDrawerOpen((open) => {
      const next = !open;
      // Lazy-load the permissions catalog the moment the first drawer
      // on the page opens. Idempotent — `ensureCatalog` short-circuits
      // when the catalog is already loaded or in-flight.
      if (next) ensureCatalog();
      return next;
    });
  }

  return (
    <li
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '12px 14px',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 220 }}>
          <span style={{ fontWeight: 600 }}>
            {name}
            {member.profile && !member.profile.is_active ? (
              <span className="pill pill-archived" style={{ marginInlineStart: 8 }}>
                غير نشط
              </span>
            ) : null}
          </span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {roleLabels.map((label, idx) => (
              <span key={`role-${idx}`} className="pill">
                {label}
              </span>
            ))}
            {member.disciplines.map((d) => (
              <span key={`disc-${d}`} className="pill pill-active">
                {disciplineLabelAr(d)}
              </span>
            ))}
          </div>
          <span className="tiny" style={{ color: 'var(--muted)' }}>
            أُضيف في {new Date(member.assigned_at).toLocaleDateString('ar-SA')}
          </span>
          {error ? (
            <span className="tiny" style={{ color: 'var(--rose)' }}>
              {error}
            </span>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Task 126 (gate) + Task 132 (un-gate) — drawer toggle now live.
              Wrap retained around the module-scope `TEAM_PERMISSION_OVERRIDES_UI_ENABLED`
              kill-switch: flipping the constant back to `false` re-gates
              the toggle in one line if a production incident surfaces. */}
          {TEAM_PERMISSION_OVERRIDES_UI_ENABLED ? (
            <button
              type="button"
              className="btn-text"
              onClick={toggleDrawer}
              style={{ padding: '8px 12px' }}
              aria-expanded={drawerOpen}
            >
              {drawerOpen ? 'إخفاء الصلاحيات' : 'إدارة الصلاحيات على هذا العميل'}
            </button>
          ) : null}
          {confirming ? (
            <>
              <button
                type="button"
                className="btn-text"
                onClick={remove}
                disabled={removing}
                style={{ padding: '8px 12px', color: 'var(--rose)' }}
              >
                {removing ? <span className="spinner" /> : null}
                <span>{removing ? 'جاري الإزالة…' : 'تأكيد الإزالة'}</span>
              </button>
              <button
                type="button"
                className="btn-text"
                onClick={() => setConfirming(false)}
                disabled={removing}
                style={{ padding: '8px 12px' }}
              >
                إلغاء
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn-text"
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
              style={{ padding: '8px 12px' }}
            >
              إزالة من الفريق
            </button>
          )}
        </div>
      </div>
      {/* Task 126 (gate) + Task 132 (un-gate) — drawer mount is live.
          The `TEAM_PERMISSION_OVERRIDES_UI_ENABLED` wrap stays as
          defense-in-depth alongside the kill-switch posture: if the
          constant is ever flipped back to `false` for an incident,
          the mount short-circuits to null without needing this JSX
          to be re-authored. `drawerOpen` is initialised false +
          `toggleDrawer` also short-circuits when the flag is off. */}
      {TEAM_PERMISSION_OVERRIDES_UI_ENABLED && drawerOpen ? (
        <PermissionsOverridesDrawer clientId={clientId} userId={member.user_id} catalog={catalog} />
      ) : null}
    </li>
  );
}

// ── Task 122A — per-member permissions drawer ────────────────────────

/**
 * Per-member drawer showing this member's NON-revoked client-scoped
 * permission overrides for THIS client, plus a form to grant a new
 * one. RLS-bound: a non-leadership caller silently sees zero rows.
 *
 * The drawer is self-contained — it owns the override list state
 * (reloads only when its own grant / revoke action completes) and
 * does NOT trigger a full team-roster refresh. Removing the member
 * from the team (the row's existing remove flow) cascades override
 * revokes server-side (CSP-1.7) and unmounts this drawer along with
 * the row.
 */
function PermissionsOverridesDrawer({
  clientId,
  userId,
  catalog,
}: {
  readonly clientId: string;
  readonly userId: string;
  readonly catalog: PermissionsCatalogState;
}) {
  const [overrides, setOverrides] = useState<ReadonlyArray<ClientTeamPermissionOverride> | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setLoadError(null);
    listClientTeamPermissions({ clientId, userId, signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setOverrides(data.overrides);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setLoadError(err);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [clientId, userId, reloadKey]);

  const refresh = () => setReloadKey((n) => n + 1);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 12,
        background: 'var(--paper-2, #f8f8f6)',
        borderRadius: 'var(--r-sm)',
        border: '1px dashed var(--line)',
      }}
    >
      <strong style={{ fontSize: 13 }}>استثناءات الصلاحيات لهذا الموظف على هذا العميل</strong>
      {loading ? (
        <span className="tiny" style={{ color: 'var(--muted)' }}>
          جاري التحميل…
        </span>
      ) : loadError ? (
        <span className="tiny" style={{ color: 'var(--rose)' }}>
          تعذّر تحميل الاستثناءات. أعد المحاولة من زر التحديث.
        </span>
      ) : overrides && overrides.length > 0 ? (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {overrides.map((o) => (
            <OverrideRow key={o.id} override={o} onRevoked={refresh} />
          ))}
        </ul>
      ) : (
        <span className="tiny" style={{ color: 'var(--muted)' }}>
          لا توجد استثناءات صلاحيات لهذا الموظف على هذا العميل.
        </span>
      )}
      <GrantOverrideForm
        clientId={clientId}
        targetUserId={userId}
        catalog={catalog}
        onGranted={refresh}
      />
    </div>
  );
}

function OverrideRow({
  override,
  onRevoked,
}: {
  readonly override: ClientTeamPermissionOverride;
  readonly onRevoked: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revoke() {
    setError(null);
    setWorking(true);
    try {
      await revokeClientTeamPermission({ input: { override_id: override.id } });
      onRevoked();
    } catch (err) {
      setWorking(false);
      setConfirming(false);
      setError(mutationErrorMessage(err));
    }
  }

  const effectPillClass = override.effect === 'allowed' ? 'pill pill-active' : 'pill pill-archived';

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '8px 10px',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-sm)',
        background: 'var(--paper, #fff)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ fontSize: 12 }}>{override.permission_key}</code>
          <span className={effectPillClass}>
            {override.effect === 'allowed' ? 'مُمنوحة' : 'مرفوضة'}
          </span>
        </div>
        <span
          className="tiny"
          style={{ color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {override.reason}
        </span>
        {error ? (
          <span className="tiny" style={{ color: 'var(--rose)' }}>
            {error}
          </span>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {confirming ? (
          <>
            <button
              type="button"
              className="btn-text"
              onClick={revoke}
              disabled={working}
              style={{ padding: '6px 10px', color: 'var(--rose)' }}
            >
              {working ? <span className="spinner" /> : null}
              <span>{working ? 'جاري الإلغاء…' : 'تأكيد الإلغاء'}</span>
            </button>
            <button
              type="button"
              className="btn-text"
              onClick={() => setConfirming(false)}
              disabled={working}
              style={{ padding: '6px 10px' }}
            >
              تراجع
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn-text"
            onClick={() => {
              setError(null);
              setConfirming(true);
            }}
            style={{ padding: '6px 10px' }}
          >
            إلغاء الاستثناء
          </button>
        )}
      </div>
    </li>
  );
}

function GrantOverrideForm({
  clientId,
  targetUserId,
  catalog,
  onGranted,
}: {
  readonly clientId: string;
  readonly targetUserId: string;
  readonly catalog: PermissionsCatalogState;
  readonly onGranted: () => void;
}) {
  const [permissionKey, setPermissionKey] = useState('');
  const [effect, setEffect] = useState<ClientTeamPermissionEffect>('allowed');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!permissionKey) {
      setError('اختر صلاحية لإضافة الاستثناء.');
      return;
    }
    if (reason.trim().length === 0) {
      setError('سبب الاستثناء مطلوب (يُسجَّل في سجل التدقيق).');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await grantClientTeamPermission({
        input: {
          client_id: clientId,
          target_user_id: targetUserId,
          permission_key: permissionKey,
          effect,
          reason,
        },
      });
      setPermissionKey('');
      setReason('');
      setEffect('allowed');
      onGranted();
    } catch (err) {
      setError(mutationErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 10,
        background: 'var(--paper, #fff)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-sm)',
      }}
    >
      <strong style={{ fontSize: 12 }}>إضافة استثناء جديد</strong>
      {catalog.status === 'loading' ? (
        <span className="tiny" style={{ color: 'var(--muted)' }}>
          جاري تحميل قائمة الصلاحيات…
        </span>
      ) : catalog.status === 'error' ? (
        <span className="tiny" style={{ color: 'var(--rose)' }}>
          تعذّر تحميل قائمة الصلاحيات. أعد فتح اللوحة لإعادة المحاولة.
        </span>
      ) : null}
      {error ? (
        <span className="tiny" style={{ color: 'var(--rose)' }}>
          {error}
        </span>
      ) : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label className="tiny" htmlFor={`override_perm_${targetUserId}`}>
          الصلاحية
        </label>
        <select
          id={`override_perm_${targetUserId}`}
          className="auth-input"
          value={permissionKey}
          onChange={(e) => setPermissionKey(e.currentTarget.value)}
          disabled={submitting || catalog.status !== 'ready'}
        >
          <option value="">— اختر صلاحية —</option>
          {catalog.catalog?.map((p) => (
            <option key={p.id} value={p.key}>
              {/* Task 133 — `public.permissions` has `description`, not
                  `name_en` / `name_ar`. The description is the
                  human-readable label per seed; key fallback covers
                  any row where description is null. Arabic label is
                  a future task (would need a migration adding a
                  translated column). */}
              {p.description || p.key}
            </option>
          ))}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <label className="tiny" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input
            type="radio"
            name={`override_effect_${targetUserId}`}
            value="allowed"
            checked={effect === 'allowed'}
            onChange={() => setEffect('allowed')}
            disabled={submitting}
          />
          مُمنوحة
        </label>
        <label className="tiny" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input
            type="radio"
            name={`override_effect_${targetUserId}`}
            value="denied"
            checked={effect === 'denied'}
            onChange={() => setEffect('denied')}
            disabled={submitting}
          />
          مرفوضة
        </label>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label className="tiny" htmlFor={`override_reason_${targetUserId}`}>
          السبب (مطلوب — يُسجَّل في سجل التدقيق)
        </label>
        <textarea
          id={`override_reason_${targetUserId}`}
          className="auth-input"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          disabled={submitting}
          rows={2}
          maxLength={2000}
        />
      </div>
      <div>
        <button
          type="submit"
          className="auth-cta"
          disabled={submitting || !permissionKey || reason.trim().length === 0}
          style={{ width: 'auto' }}
        >
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري الإضافة…' : 'إضافة الاستثناء'}</span>
        </button>
      </div>
    </form>
  );
}

// ── Add member ───────────────────────────────────────────────────────

function AddMemberForm({
  clientId,
  pool,
  onAdded,
}: {
  readonly clientId: string;
  readonly pool: ReadonlyArray<AvailableEmployee>;
  readonly onAdded: () => void;
}) {
  const [selected, setSelected] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!selected) {
      setError('اختر موظفاً لإضافته للفريق.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await assignClientTeamMember({
        input: { client_id: clientId, target_user_id: selected },
      });
      setSelected('');
      onAdded();
    } catch (err) {
      setError(mutationErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>إضافة عضو للفريق</h2>
        <span className="meta">client_assignments</span>
      </div>
      {pool.length === 0 ? (
        <EmptyState
          title="لا يوجد موظفون متاحون للإضافة"
          description="كل موظفي الوكالة الحاليين معيّنون لهذا العميل بالفعل، أو لا يوجد موظفون في الوكالة بعد. أضف موظفين عبر صفحة الفريق والصلاحيات."
        />
      ) : (
        <form onSubmit={onSubmit} noValidate>
          {error ? (
            <div className="banner banner-error" style={{ marginBottom: 14 }}>
              {error}
            </div>
          ) : null}
          <div className="auth-field" style={{ marginBottom: 0 }}>
            <label className="auth-field-label" htmlFor="client_team_add_member">
              موظف الوكالة
            </label>
            <select
              id="client_team_add_member"
              className="auth-input"
              value={selected}
              onChange={(e) => setSelected(e.currentTarget.value)}
              disabled={submitting}
              style={{ paddingInlineStart: 18 }}
            >
              <option value="">— اختر موظفاً —</option>
              {pool.map((emp) => (
                <option key={emp.userId} value={emp.userId}>
                  {emp.roleLabels.length > 0
                    ? `${emp.label} — ${emp.roleLabels.join(', ')}`
                    : emp.label}
                </option>
              ))}
            </select>
          </div>
          {/*
           * Task 125 — name the operation's invariants in user-facing
           * copy so the leader doesn't have to infer the mental model:
           *   1. employee already exists (no invitation is sent),
           *   2. assignment grants access to THIS client only,
           *   3. the employee's global role is unchanged.
           * The wording also implicitly rules out the "this changes
           * permissions" misread that Task 122A's separate per-client
           * override drawer DOES handle.
           */}
          <p
            className="tiny"
            style={{ color: 'var(--muted)', marginTop: 14, marginBottom: 0, lineHeight: 1.6 }}
          >
            هذا الموظف موجود في الوكالة بالفعل — لن تُرسل دعوة. التعيين هنا يفتح وصول هذا العميل فقط
            (حسب صلاحيات دوره)، ولا يغيّر دوره العام في الوكالة ولا أيًّا من تعييناته للعملاء
            الآخرين.
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button
              type="submit"
              className="auth-cta"
              disabled={submitting || !selected}
              style={{ width: 'auto' }}
            >
              {submitting ? <span className="spinner" /> : null}
              <span>{submitting ? 'جاري الإضافة…' : 'إضافة للفريق'}</span>
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * The pool of agency employees available to add — every agency-category
 * user in the org, deduped by `user_id` (a user may hold more than one
 * role), minus the ones already on this client's team.
 *
 * Task 125 — collect ALL agency-role labels per user so the dropdown
 * option text can show the role(s) alongside the name. A user can hold
 * multiple agency roles (e.g. senior_designer + production_manager);
 * we keep the unique labels in insertion order and render them as
 * comma-separated text after the name. Empty list means "agency-role
 * user but no human-readable label" — fall back to nothing rather than
 * mislabel.
 */
function buildAvailablePool(
  userRoles: ReadonlyArray<UserRoleRow>,
  teamUserIds: ReadonlySet<string>,
): ReadonlyArray<AvailableEmployee> {
  const byUser = new Map<string, { userId: string; label: string; roleLabels: string[] }>();
  for (const ur of userRoles) {
    if (ur.role?.category !== 'agency') continue;
    if (teamUserIds.has(ur.user_id)) continue;
    // Task 114 fix — drop entries whose profile is inactive (or
    // missing). The EF's preflight `profiles?...&is_active=eq.true`
    // would reject them with 404; surfacing them in the dropdown only
    // to fail-on-submit is a confusing UX. The dropdown now matches
    // the EF's gate.
    if (!ur.profile || ur.profile.is_active === false) continue;
    const roleLabel = (ur.role.name_ar || ur.role.name_en || ur.role.key).trim();
    const existing = byUser.get(ur.user_id);
    if (existing) {
      if (roleLabel && !existing.roleLabels.includes(roleLabel)) {
        existing.roleLabels.push(roleLabel);
      }
      continue;
    }
    const label = ur.profile.full_name?.trim() || ur.profile.email || ur.user_id;
    byUser.set(ur.user_id, {
      userId: ur.user_id,
      label,
      roleLabels: roleLabel ? [roleLabel] : [],
    });
  }
  return Array.from(byUser.values())
    .map((entry) => ({
      userId: entry.userId,
      label: entry.label,
      roleLabels: entry.roleLabels as ReadonlyArray<string>,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ar'));
}

function disciplineLabelAr(discipline: ContentDiscipline): string {
  switch (discipline) {
    case 'content':
      return 'محتوى';
    case 'design':
      return 'تصميم';
    case 'video':
      return 'فيديو';
    default:
      return discipline;
  }
}

function mutationErrorMessage(err: unknown): string {
  if (err instanceof KmiApiError) {
    if (err.code === 'conflict') {
      // Task 114 — `conflict` is reserved for the active-uniq partial
      // index hit (Postgres 23505) only; the EF returns it with
      // `detail: 'duplicate'` (assign-client-team-member, "already on
      // team") or `detail: 'duplicate_override'` (Task 122A
      // grant-client-team-permission, "override already exists"). The
      // detail check makes the two cases route to distinct copy
      // without re-introspecting the calling EF.
      const detail = typeof err.body.detail === 'string' ? err.body.detail : null;
      if (detail === 'duplicate') {
        return 'هذا الموظف عضو في فريق العميل بالفعل.';
      }
      if (detail === 'duplicate_override') {
        return 'هذا الموظف لديه استثناء فعّال لنفس الصلاحية على هذا العميل. ارفع الاستثناء الحالي قبل إضافة جديد.';
      }
      return 'تعذّر إكمال العملية بسبب تعارض في البيانات.';
    }
    if (err.code === 'permission_denied') return 'لا تملك صلاحية إدارة فريق هذا العميل.';
    if (err.code === 'not_found') return 'العميل أو الموظف غير متاح.';
    if (err.code === 'validation_error') {
      // Task 122A — the grant-client-team-permission EF rejects with
      // `target_user_id: ['not_assigned_to_client']` when the target
      // is not actively on the client's team. Surface a precise
      // message so the leader can investigate / assign first rather
      // than retry blindly.
      const targetUserField = err.body.fields?.target_user_id;
      if (targetUserField && targetUserField.includes('not_assigned_to_client')) {
        return 'هذا الموظف ليس مُعيَّناً لهذا العميل حالياً — أضِفه للفريق أولاً ثم أضِف الاستثناء.';
      }
      // Covers both Zod-rejected bodies (rare — FE always sends
      // well-formed UUIDs from URL params) and the trigger
      // `validate_client_assignment_scope` rejection on cross-org /
      // inactive-profile / sysadmin targets.
      return 'تعذّر إكمال العملية: الموظف يجب أن يكون موظف وكالة نشطاً.';
    }
    if (err.code === 'unauthenticated') return 'انتهت الجلسة. سيتم إعادة تسجيل الدخول…';
    if (err.code === 'internal_error') {
      // Task 114 — surface a specific message when the EF cannot
      // complete the operation server-side (rather than the catch-all).
      // Production was getting this when the broken `_shared/db.ts`
      // transport failed; with that path removed, this branch will be
      // rare but should still surface clearly when it fires.
      return 'حدث خطأ في الخادم. حاول مجدداً بعد قليل.';
    }
  }
  return 'تعذّر إكمال العملية. حاول مجدداً.';
}
